import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { InputOption } from '@app/models/input-option';
import { ControlTypeEnum, SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { RadioInputGroupComponent } from '@app/shared/inputs/radio-input-group/radio-input-group.component';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { asapScheduler, first, skip } from 'rxjs';

@Component({
  selector: 'app-hypertension',
  templateUrl: './hypertension.component.html',
  styleUrls: ['./hypertension.component.css']
})
export class HypertensionComponent implements OnInit {

  @ViewChild('childDiagnosisComponent') childDiagnosisComponent: RadioInputGroupComponent;

  @Input() indication: IndicationDefinition;
  
  allIndications: IndicationGroup[] = [];

  childDiagnosisSelectionModel: SelectionsModel;

  systolicLabel: string = 'Systolic BP Lower Limit';
  diastolicLabel: string = 'Diastolic BP Lower Limit';

  systolicVariableName: string = 'systolicBpLowerLimit';
  diastolicVariableName: string = 'diastolicBpLowerLimit';

  hypertensionInputOptions: InputOption[][] = [
    [
      {name: 'AND', displayName: 'AND', order: 1},
      {name: 'OR', displayName: 'OR', order: 2}
    ],
    [
      {name: this.systolicVariableName, displayName: this.systolicLabel, order: 1, defaultValue: 140},
      {name: this.diastolicVariableName, displayName: this.diastolicLabel, order: 2, defaultValue: 90}
    ]
  ];

  public formGroup: FormGroup = this.getDefaultFormGroup();

  constructor(
    private sharedService: SharedService
  ) { }

  ngOnInit() {
    const updatedSystolicLabel = 'Systolic BP (' + this.indication.units + ') Lower Limit ' + '(>=)';
    const updatedDiastolicLabel = 'Diastolic BP (' + this.indication.units + ') Lower Limit ' + '(>=)';

    this.hypertensionInputOptions[1][0].displayName = updatedSystolicLabel;
    this.hypertensionInputOptions[1][1].displayName = updatedDiastolicLabel;

    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });

    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      asapScheduler.schedule(() => {
        if (this.indication.selections.some(i => i.controlName === 'diagnosis')) {
          if (this.childDiagnosisComponent) {
            this.childDiagnosisComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'diagnosis').selectionValue);
          }
        }
        if (this.formGroup) {
          if (this.indication.selections.some(i => i.controlName === this.systolicVariableName)){
            this.formGroup.controls.systolicBpLowerLimit.setValue(this.indication.selections.find(i => i.controlName === this.systolicVariableName).selectionValue);
          }
          if (this.indication.selections.some(i => i.controlName === 'condition')){
            this.formGroup.controls.condition.setValue(this.indication.selections.find(i => i.controlName === 'condition').selectionValue);
          }
          if (this.indication.selections.some(i => i.controlName === this.diastolicVariableName)){
            this.formGroup.controls.diastolicBpLowerLimit.setValue(this.indication.selections.find(i => i.controlName === this.diastolicVariableName).selectionValue);
          }
        }
        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  childSharedControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childDiagnosisSelectionModel = selectionModel;
    if (!this.showCriteriaSection()) {
      this.formGroup = this.getDefaultFormGroup();
    }
    this.updateSelections();
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.childDiagnosisComponent.overrideInputValue('DU');
      this.formGroup = this.getDefaultFormGroup();
      this.updateSelections();
    });
  }

  getDefaultFormGroup(): FormGroup {
    const fg = new FormGroup({
      systolicBpLowerLimit: new FormControl(140, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ]),
      condition: new FormControl('OR', [
        Validators.required
      ]),
      diastolicBpLowerLimit: new FormControl(90, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ]),
    });
    return fg;
  }

  showCriteriaSection(): boolean {
    const visible = this.childDiagnosisSelectionModel && 
    !vennHealthConstants.diagnosedOrTreatedOnlyCodes.includes(this.childDiagnosisSelectionModel.selectionValue);
    return visible;
  }

  updateSelections(): void {
    const currentSelections: SelectionsModel[] = [];
    if (this.childDiagnosisSelectionModel) {
      currentSelections.push(this.childDiagnosisSelectionModel);
    }

    const currentSystolicSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: this.systolicVariableName,
      controlType: ControlTypeEnum.TEXT,
      controlDisplayName: this.systolicLabel,
      order: 2,
      selectionValue: this.formGroup.controls.systolicBpLowerLimit.value,
      selectionDisplayName: ''
    };
    currentSelections.push(currentSystolicSelection);

    const currentConditionSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: 'condition',
      controlType: ControlTypeEnum.CUSTOM,
      controlDisplayName: '',
      order: 3,
      selectionValue: this.formGroup.controls.condition.value,
      selectionDisplayName: ''
    };
    currentSelections.push(currentConditionSelection);

    const currentDiastolicSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: this.diastolicVariableName,
      controlType: ControlTypeEnum.TEXT,
      controlDisplayName: this.diastolicLabel,
      order: 4,
      selectionValue: this.formGroup.controls.diastolicBpLowerLimit.value,
      selectionDisplayName: ''
    };
    currentSelections.push(currentDiastolicSelection);
    
    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }

}
